import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Input, Type, input, output, signal, TemplateRef } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogConfig, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { UtcToLocalPipe, PlanTypeDescriptionPipe } from '../../pipes';
import { StatusBadgeClassPipe } from '../../pipes/status-badge-class.pipe';
import { ListComponent } from '../../../core/components/list/list.component';
import { ViewComponent } from '../../../core/components/view/view.component';
import { Column, Filter, PaginateResult } from '../../../core/models';
import { ApiService } from '../../../services';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ControlContainer, FormsModule } from '@angular/forms';
import { of, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TooltipModule } from 'primeng/tooltip';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'app-one-to-many',
  imports: [
    CommonModule,
    DynamicDialogModule,
    ButtonModule,
    TableModule,
    ToastModule,
    UtcToLocalPipe,
    TooltipModule,
    PlanTypeDescriptionPipe,
    StatusBadgeClassPipe,
    FormsModule,
    RadioButtonModule,
    DialogModule,
  ],
  templateUrl: './one-to-many.component.html',
  styleUrl: './one-to-many.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OneToManyComponent extends ListComponent {
  @Input({ required: true })
  override defaultSort!: { [key: string]: string };

  @Input({ required: true })
  override service!: ApiService;

  @Input({ required: true })
  componentType!: Type<any>;

  @Input({ required: true })
  cols!: Column[];

  @Input({ required: true })
  title!: string;

  /** Optional header for the modal when editing an existing row (e.g. "Edit Family Member"). */
  @Input()
  editTitle?: string;

  @Input({ required: true })
  override fields!: string[];

  @Input({ required: true })
  id!: string;

  @Input({ required: true })
  parentIdKey!: string;

  @Input({ required: true })
  foreingKeyName!: string;

  @Input()
  isLimitedPagination!: boolean;

  @Input()
  limitedCount!: number;

  @Input()
  hasFile!: boolean;

  @Input()
  statusUpdateAction?: (row: any) => void;

  @Input()
  entityType?: string;

  @Input()
  isViewOnly? = false;

  @Input()
  showDeleteBtn? = false;

  @Input()
  showNewBtn? = true;

  @Input()
  showEditBtn? = true;

  /**
   * When true, `edit` opens the dialog with `row` + `parentId` only (no `id`).
   * Use for flows where the child editor should not call GET-by-id (e.g. file upload / blob replace via the same Upload route).
   */
  @Input()
  editWithoutFetchId? = false;

  @Input()
  showIncomplete? = true;

  @Input()
  referenceValueFromList?: string;

  @Input()
  afterSave?: (data: any, list: any) => Promise<any>;

  @Input()
  customDelete?: (id: any, list: any, index?: number) => Promise<void>;

  @Input()
  width? = '80%';

  @Input()
  height? = '80%';

  @Input()
  override manySelectable: boolean = false;

  @Input()
  override singleSelectable: boolean = false;

  @Input()
  extraFilters: Filter[] = [];

  public override selectedRowsData: any[] = [];

  public selectedRadio: any;

  @Input()
  customShow?: () => void;

  @Input()
  customView?: (rowData: any) => void;

  @Input()
  customLoad?: (event: TableLazyLoadEvent) => Subscription;

  @Input()
  includeExpiredQuotes?: boolean = false;

  beforeNew = output<DynamicDialogConfig>();

  editChange = output();
  deleteChange = output();
  onLoadChange = output<TableLazyLoadEvent>();
  onDataChange = output<PaginateResult>();

  private defaultConfig!: DynamicDialogConfig;
  private parentId!: string;

  modalTitle?: string;
  modalVisible = false;
  modalText: string | null = null;
  modalTemplate?: TemplateRef<any>;
  modalContext: any;

  constructor(
    public override router: Router,
    public override messageService: MessageService,
    public override confirmationService: ConfirmationService,
    private _dialogService: DialogService,
    private _dialogRef: DynamicDialogRef | null,
    private _controlContainer: ControlContainer,
    private _destroyRef: DestroyRef,
  ) {
    super({} as any, router, {}, confirmationService, messageService);
  }

  override ngOnInit(): void {
    this.parentId = this._controlContainer.value[this.parentIdKey];
    this.defaultConfig = {
      header: this.title,
      width: this.width,
      height: this.height,
      modal: true,
      data: {
        parentId: this.parentId,
        row: { [this.foreingKeyName]: this.parentId },
      },
    };
    if (this.entityType) {
      this.defaultConfig.data.row.entityType = this.entityType;
    }
    this.paginateQuery.filters = [...this.getDefaultFilters(this.parentId, this.fields, 1, 'AND'), ...this.extraFilters];
    this.paginateQuery.includeIncomplete = this.showIncomplete;
  }

  override load(event: TableLazyLoadEvent) {
    if (this.customLoad) {
      return this.customLoad(event);
    }

    if (this.parentId) {
      if (this.includeExpiredQuotes) {
        this.paginateQuery.includeExpired = true;
      }

      const subscription = super.load(event);
      if (subscription) {
        subscription.add(() => {
          this.onDataChange.emit(this.data);
        });
      }
      return subscription;
    }

    const list = this._controlContainer.value[this.referenceValueFromList!] ?? [];
    return of({ total: list.length, list })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.data = res;
        this.loading.set(false);
        this.changeDetector.markForCheck();
        this.onDataChange.emit(this.data);
      });
  }

  override hasPermissionEdit() {
    if (this.hasFile) {
      return this.hasPermissionToUploadFile();
    }
    return super.hasPermissionEdit();
  }

  show() {
    if (this.customShow) {
      this.customShow();
    } else {
      this.beforeNew.emit(this.defaultConfig);
      this._dialogRef = this._dialogService.open(this.componentType, this.defaultConfig);
      this.onClose();
    }
  }

  override edit(row: any, index: any) {
    if (!this.showEditBtn) {
      return;
    }
    if (this.customView) {
      this.customView(row);
      return;
    }

    row[this.foreingKeyName] = this.parentId;
    const baseConfig = this.defaultConfig ?? {
      header: this.title,
      width: this.width,
      height: this.height,
      modal: true,
      data: {
        parentId: this.parentId,
        row: { [this.foreingKeyName]: this.parentId },
      },
    };
    const dialogData: Record<string, unknown> = {
      row,
      parentId: this.parentId,
    };
    if (!this.editWithoutFetchId) {
      dialogData['id'] = row[this.id];
    }
    this._dialogRef = this._dialogService.open(this.componentType, {
      ...baseConfig,
      header: this.editTitle ?? baseConfig.header,
      data: dialogData,
    });
    this.onClose(index);
  }

  openViewOnly(id: string) {
    this.service
      ?.getByViewId(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this._dialogService.open(ViewComponent, {
          header: this.title,
          width: this.width,
          height: this.height,
          modal: true,
          data: {
            dataList: value,
          },
        });
      });
  }

  private onData(data: PaginateResult, index?: number) {
    if (index !== undefined) {
      this.data.list[index] = data;
    } else {
      this.data.list.push(data);
    }
    this.data.total = this.data.list.length;
    this.onDataChange.emit(this.data);
  }

  private onClose(index?: number) {
    this._dialogRef?.onClose.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((data) => {
      if (this.hasFile) {
        this.fetchData();
        return;
      }
      if (data) {
        const action = () => (this.parentId ? this.fetchData() : this.onData(data, index));
        if (this.afterSave) {
          this.afterSave(data, this.data.list).then(action);
        } else {
          action();
        }
      }
    });
  }

  private hasPermissionToUploadFile() {
    return this.tokenService.isAdmin() || this.tokenService.isContributor();
  }

  override delete(id: string, index?: number): void {
    if (this.customDelete) {
      this.customDelete(id, this.data.list, index).then(() => this.fetchData());
    } else if (this.parentId) {
      super.delete(id);
    } else if (index != undefined && index >= 0) {
      this.data.list.splice(index, 1);
      this.changeDetector.markForCheck();
    }
  }

  onSelectionCheckboxClick(event: Event, rowData: any) {
    this.selectedRowsData.push(rowData);
  }

  onSingleSelectionRadioClick(event: Event, rowData: any) {
    this.selectedRadio = rowData;
  }

  onStatusUpdateClick(event: Event, rowData: any) {
    event.stopPropagation();
    if (this.statusUpdateAction) {
      this.statusUpdateAction(rowData);
    }
  }

  updateData(newData: PaginateResult) {
    this.data = newData;
    this.changeDetector.markForCheck();
  }

  get currentSelection() {
    return this.manySelectable ? this.selectedRowsData : this.selectedRadio;
  }

  set currentSelection(value: any) {
    if (this.manySelectable) {
      this.selectedRowsData = value;
    } else {
      this.selectedRadio = value;
    }
  }

  openModal(event: Event, row: any, column: Column) {
    event.stopPropagation();

    const value = row[column.field];

    const colWithTemplate = column as Column & {
      modalTemplate?: TemplateRef<any>;
    };

    if (colWithTemplate.modalTemplate) {
      this.modalTemplate = colWithTemplate.modalTemplate;
      this.modalContext = {
        row,
        value,
        column,
      };
      this.modalText = null;
    } else {
      this.modalTemplate = undefined;
      this.modalContext = null;
      this.modalText = value ?? '';
    }
    this.modalTitle = column.modalTitle;
    this.modalVisible = true;
  }

  override confirmDelete(event: Event, row: any, id: string, index?: number): void {
    event.stopPropagation();
    super.confirmDelete(event, row, id, index);
  }

  downloadFile(event: Event, row: any) {
    event.stopPropagation();
    super.download(row);
  }

  openLink(event: Event, url: string): void {
    event.stopPropagation();
    if (!url || url === '--') {
      return;
    }
    window.open(url, '_blank');
  }
}
